import React from "react";
import { useSnapshot } from "valtio";
import { useTranslation } from "react-i18next";
import swal from "@sweetalert/with-react";

import {
    EyeIcon,
    RefreshIcon,
    StyledButton,
    StyledIconButton,
    CrossIcon,
} from "@aureskonnect/react-ui";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import { v4 as uuid } from "uuid";

import {
    computedStore,
    generalConfigStore,
    setActiveLanguages,
    setChosenTemplate,
    setConfigOptionsCustomerAccount,
    setDefaultLanguage,
    setDefaultWays,
    setFiles,
    setGeneralChosenTransition,
    setGeneralTransition,
    setImageKey,
    setInformationMessageItem,
    setInitItemsFromGeneralSetting,
    setIsConfigured,
    setIsCustomerAccountActive,
    setIsInformationModesEmpty,
    setIsSpecificTransitionChosen,
    setIsStateChanged,
    setItems,
    setProject,
    setSelectedLanguage,
    setSelectedSaleModeValue,
    setVisualizedTemplate,
    store,
} from "@store";
import { store as VerticalLayoutStore } from "../../../components/VerticalLayout/store";
import { FlexboxGrid } from "@components/Common/FlexboxGrid";
import { SwiperWrapper } from "../../../components/FullscreenPreview/SwiperWrapper";
import { Modal as Modala } from "../../../components/Common/ModalWrapper/Modal";

import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { moveArrayElementToTheBegin } from "@helpers/general";
import {
    ACCOUNT_CREATION_ID,
    ACCOUNT_CREATION_ITEM,
    CLICK_AND_COLLECT,
    CLICK_AND_COLLECT_ID,
    CREDIT_CARD_SHORT_NAME,
    CUSTOMER_ACCOUNT,
    CUSTOMER_ACCOUNT_ID,
    CUSTOMER_ACCOUNT_PDC,
    CUSTOMER_BENEFIT_INITIAL_STATE,
    DEFAULT_ELEMENTS_PAGES,
    ELEMENTS_PAGES,
    GENERAL_TRANSITION_INITIAL_STATE,
    GODIA_LOYALTY,
    LOGO,
    LOYALTY_ACCOUNT,
    NONE,
    VALID_WAYS_PAGES,
    WAYS_PAGES,
} from "../../../constants/index";
import { useSelector } from "react-redux";
import { useFetchGlobal } from "../../../api/hooks";

type PropsType = {
    template: NewTemplateType;
};

export function Template({ template }: PropsType): JSX.Element {
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();

    const { chosenTemplate, project, isConfigured } = useSnapshot(
        generalConfigStore
    );
    const { items } = useSnapshot(computedStore);
    const { images } = useSnapshot(store);
    const mainColor = useSelector(
        (state: any) => state.global.infoUser.maincolor
    );
    const { franchiseID, shopID, oneShop, fideltyType } = useSnapshot(
        VerticalLayoutStore
    );
    const shop_id = oneShop
        ? shopID
        : project.shopId !== undefined
        ? project.shopId
        : shopID;
    const [isModalOpened, setIsModalOpened] = React.useState<boolean>(false);
    const [isAlertModalOpened, setIsAlertModalOpened] = React.useState<boolean>(
        false
    );
    const [isModalShown, setModalVisibility] = React.useState(false);

    function alertToggle() {
        setIsAlertModalOpened(!isAlertModalOpened);
    }
    function toggle() {
        setIsModalOpened(!isModalOpened);
    }

    async function handleChosenTemplateOnclickEvent() {
        try {
            const savedData = {
                franchiseId: franchiseID,
                shopId: shop_id,
            };

            const queryString = new URLSearchParams(savedData).toString();
            const data: any = await fetchGlobal(
                `${process.env.REACT_APP_API_V2_URL}/settings/generals/data?${queryString}`,
                {
                    method: "GET",
                }
            );
            if (!data?.data?.[shop_id]) {
                console.error("Shop data not found");
                swal({
                    icon: "error",
                    content: <p>{t("Failed to load shop configuration")}</p>,
                    buttons: false,
                    timer: 2000,
                });
                throw new Error("Shop data not found");
            }
            const {
                setting_sale_mode,
                setting_language,
                setting_Payment_Method,
                setting_Information_mode,
                setting_Logo,
            } = data.data[shop_id];

            if (
                setting_sale_mode.filter(
                    (item: any) =>
                        item.active === true && item.id !== CLICK_AND_COLLECT_ID
                ).length === 0 ||
                setting_language.filter((item: any) => item.active === true)
                    .length === 0
            ) {
                setIsAlertModalOpened(!isAlertModalOpened);
            } else {
                let templateFiles = images.filter(
                    (image: ImageType) => image.id === template.id
                )[0].files;

                if (
                    setting_Information_mode.length === 0 ||
                    setting_Information_mode.filter(
                        (item: ProjectMainContentItemType) => {
                            return setting_sale_mode.filter(
                                (saleMethod: any) => {
                                    return (
                                        Object.keys(item.active).includes(
                                            saleMethod.name
                                        ) && saleMethod.active === true
                                    );
                                }
                            );
                        }
                    ).length === 0
                ) {
                    setIsInformationModesEmpty(true);
                }

                if (setting_Information_mode.length > 0) {
                    setIsInformationModesEmpty(false);
                }
                const indexOfCustomerAccountOption = (template.content
                    .options as OptionsType).items.findIndex(
                    (item: OptionsItemType) => item.id === CUSTOMER_ACCOUNT_ID
                );
                let coptyItems: OptionsItemType[] = [
                    ...(template.content.options as OptionsType).items,
                ];
                if (
                    (template.content.options as OptionsType).items[
                        indexOfCustomerAccountOption
                    ].title === CUSTOMER_ACCOUNT
                ) {
                    coptyItems[indexOfCustomerAccountOption] = {
                        ...coptyItems[indexOfCustomerAccountOption],
                        languages: {
                            en: {
                                ...(template.content.options as OptionsType)
                                    .items[indexOfCustomerAccountOption]
                                    .languages.en,
                                name: LOYALTY_ACCOUNT,
                            },
                            es: {
                                ...(template.content.options as OptionsType)
                                    .items[indexOfCustomerAccountOption]
                                    .languages.es,
                                name: "Cuenta de fidelización",
                            },
                            fr: {
                                ...(template.content.options as OptionsType)
                                    .items[indexOfCustomerAccountOption]
                                    .languages.fr,
                                name: "Compte fidélité",
                            },
                            it: {
                                ...(template.content.options as OptionsType)
                                    .items[indexOfCustomerAccountOption]
                                    .languages.it,
                                name: "Conto fedeltà",
                            },
                            nl: {
                                ...(template.content.options as OptionsType)
                                    .items[indexOfCustomerAccountOption]
                                    .languages.nl,
                                name: "Treuekonto",
                            },
                            pt: {
                                ...(template.content.options as OptionsType)
                                    .items[indexOfCustomerAccountOption]
                                    .languages.pt,
                                name: "Conta de fidelidade",
                            },
                        },
                    };
                }

                const customerAccountObject = (template.pages.elements
                    .orderTaking as OrderTakingPageType).design.items.findIndex(
                    (item: any) => item.id === CUSTOMER_ACCOUNT_PDC
                );
                const copyDesignItems = [
                    ...(template.pages.elements
                        .orderTaking as OrderTakingPageType).design.items,
                ];
                if (
                    (template.pages.elements.orderTaking as OrderTakingPageType)
                        .design.items[customerAccountObject].name ===
                    LOYALTY_ACCOUNT
                ) {
                    copyDesignItems[customerAccountObject] = {
                        ...copyDesignItems[customerAccountObject],
                        name: CUSTOMER_ACCOUNT,
                    };
                }

                let authenticationModesOptions = (template.content
                    .authenticationModes as ProjectContentItemType).items;

                if (fideltyType[shop_id] === GODIA_LOYALTY) {
                    if (
                        (template.content
                            .connection as ProjectContentItemType).items.find(
                            (item: any) => item.id === ACCOUNT_CREATION_ID
                        ) === undefined
                    ) {
                        (template.content
                            .connection as ProjectContentItemType).items.unshift(
                            ACCOUNT_CREATION_ITEM
                        );
                    }
                } else {
                    (template.content
                        .connection as ProjectContentItemType).items.filter(
                        (item: any) => item.id !== ACCOUNT_CREATION_ID
                    );
                }
                let informationModesPage =
                    (template.pages.elements.informationModes as PageType)
                        .isInformationModeActivePerCategory !== undefined
                        ? {
                              ...(template.pages.elements
                                  .informationModes as PageType),
                          }
                        : {
                              ...(template.pages.elements
                                  .informationModes as PageType),
                              isInformationModeActivePerCategory: false,
                          };
                template = {
                    ...template,
                    pages: {
                        ...template.pages,
                        elements: {
                            ...template.pages.elements,
                            orderTaking: {
                                ...(template.pages.elements
                                    .orderTaking as OrderTakingPageType),
                                design: {
                                    ...(template.pages.elements
                                        .orderTaking as OrderTakingPageType)
                                        .design,
                                    items: copyDesignItems as ActionsButtonsDesignItemType[],
                                },
                            },
                            informationModes: informationModesPage,
                        },
                    },
                    content: {
                        ...template.content,
                        authenticationModes: {
                            ...project.template.content.authenticationModes,
                            items: authenticationModesOptions,
                        },
                        options: {
                            ...(template.content.options as OptionsType),
                            items: coptyItems,
                        },
                        languages: {
                            ...template.content.languages,
                            items:
                                setting_language.filter(
                                    (language: LanguagesItemType) =>
                                        language.active === true &&
                                        language.isDefault === false
                                ).length > 1
                                    ? moveArrayElementToTheBegin(
                                          setting_language.find(
                                              (language: LanguagesItemType) =>
                                                  language.isDefault === true
                                          ),
                                          moveArrayElementToTheBegin(
                                              setting_language.find(
                                                  (
                                                      language: LanguagesItemType
                                                  ) =>
                                                      language.active ===
                                                          true &&
                                                      language.isDefault ===
                                                          false
                                              ),
                                              setting_language
                                          )
                                      )
                                    : setting_language,
                        },
                        salesMethods: {
                            items: setting_sale_mode,
                        },
                        meansOfPayment: {
                            items: setting_Payment_Method,
                        },
                        informationModes: {
                            items: setting_Information_mode,
                        },
                        generalDesign: {
                            ...template.content.generalDesign,
                            logo: setting_Logo.logo,
                        },
                    },
                };
                setInitItemsFromGeneralSetting({
                    informationModes: setting_Information_mode,
                    saleMethods: setting_sale_mode,
                    reglements: setting_Payment_Method,
                    languages: setting_language,
                    logo: setting_Logo.logo,
                });
                setting_sale_mode.map((item: ProjectMainContentItemType) => {
                    const data = {
                        id: item.id,
                        name: item.name,
                        content: Object.fromEntries(
                            Object.entries(item.languages).map(
                                ([key, value]) => [
                                    key,
                                    {
                                        path: value.content as string,
                                        defaultImage: value.content,
                                    },
                                ]
                            )
                        ),
                    };
                    return (templateFiles = [...templateFiles, data]);
                });
                setting_Information_mode.map(
                    (item: ProjectMainContentItemType) => {
                        const data = {
                            id: item.id,
                            name: item.name,
                            content: Object.fromEntries(
                                Object.entries(item.languages).map(
                                    ([key, value]) => [
                                        key,
                                        {
                                            path: value.content as string,
                                            defaultImage: value.content,
                                        },
                                    ]
                                )
                            ),
                        };
                        return (templateFiles = [...templateFiles, data]);
                    }
                );
                setting_language.map((item: LanguagesItemType) => {
                    const data = {
                        id: item.id,
                        name: item.name,
                        content: item.flag,
                        defaultImage: item.flag,
                    };

                    for (let file of templateFiles) {
                        if (file.content !== undefined) {
                            if (typeof file.content === "object") {
                                // eslint-disable-next-line no-prototype-builtins
                                if (
                                    file.content.hasOwnProperty(item.name) ===
                                    false
                                ) {
                                    const index = templateFiles.indexOf(file);
                                    if (index > -1) {
                                        templateFiles[index] = {
                                            ...templateFiles[index],
                                            content: {
                                                ...file.content,
                                                [item.name]: file.content.fr,
                                            },
                                        };
                                    }
                                }
                            }
                        }
                    }

                    return (templateFiles = [...templateFiles, data]);
                });
                setting_Payment_Method
                    .filter(
                        (item: ProjectMainContentItemType) =>
                            item.name !== "At the counter" &&
                            item.name !== "BDP"
                    )
                    .map((item: ProjectMainContentItemType) => {
                        const data = {
                            id: item.id,
                            name: item.name,
                            content: Object.fromEntries(
                                Object.entries(item.languages).map(
                                    ([key, value]) => [
                                        key,
                                        {
                                            path: value.content as string,
                                            defaultImage: value.content,
                                        },
                                    ]
                                )
                            ),
                        };
                        return (templateFiles = [...templateFiles, data]);
                    });

                const newLogoData = {
                    id: setting_Logo.id,
                    key: LOGO,
                    name: setting_Logo.logo.name,
                    content: setting_Logo.logo.content,
                    defaultImage: setting_Logo.logo.content,
                };

                const index = templateFiles.findIndex(
                    (item) => item.id === setting_Logo.id
                );
                if (index === -1) {
                    templateFiles = [...templateFiles, newLogoData];
                } else {
                    templateFiles[index] = newLogoData;
                }

                setChosenTemplate(template, templateFiles);
                setProject({ ...project, template, files: templateFiles });
                setFiles(templateFiles);
                setDefaultLanguage(
                    (template.content.languages as LanguagesType).items.filter(
                        (language) => language.isDefault === true
                    )[0]
                );
                setSelectedLanguage(
                    (template.content.languages as LanguagesType).items.filter(
                        (language) => language.isDefault === true
                    )[0].name
                );
                setActiveLanguages(
                    moveArrayElementToTheBegin(
                        (template.content
                            .languages as LanguagesType).items.filter(
                            (element: LanguagesItemType): boolean =>
                                element.isDefault === true
                        )[0],
                        (template.content
                            .languages as LanguagesType).items.filter(
                            (language) => language.active === true
                        )
                    )
                );
                const selectedSaleMethodDeliveryExist = (template.content
                    .salesMethods as ProjectContentItemType).items.filter(
                    (sale: any) => sale.name === "Delivery"
                )[0];
                if (
                    selectedSaleMethodDeliveryExist !== undefined &&
                    selectedSaleMethodDeliveryExist.active === true
                ) {
                    setIsCustomerAccountActive(true);

                    setIsStateChanged(true);
                    setConfigOptionsCustomerAccount(true);
                } else {
                    setIsCustomerAccountActive(
                        (template.content.options as OptionsType).items.filter(
                            (item) => item.title === CUSTOMER_ACCOUNT
                        )[0].active
                    );
                }
                const filterInformationModeInSaleMethod = (template.content
                    .salesMethods as ProjectContentItemType).items.filter(
                    (saleMethod) => {
                        return (
                            saleMethod.name !== CLICK_AND_COLLECT &&
                            (saleMethod.informationModes as InformationModesContentType)
                                .items.length !== 0 &&
                            saleMethod.active === true
                        );
                    }
                );
                if (
                    (template.content
                        .informationModes as ProjectContentItemType).items
                        .length === 0 ||
                    filterInformationModeInSaleMethod.length === 0
                ) {
                    setSelectedSaleModeValue(
                        (template.content
                            .salesMethods as ProjectContentItemType).items.filter(
                            (saleMethod) => {
                                return (
                                    saleMethod.name !== CLICK_AND_COLLECT &&
                                    saleMethod.active === true
                                );
                            }
                        )[0].name
                    );
                } else {
                    setSelectedSaleModeValue(
                        (template.content
                            .salesMethods as ProjectContentItemType).items.filter(
                            (saleMethod) => {
                                return (
                                    saleMethod.name !== CLICK_AND_COLLECT &&
                                    (saleMethod.informationModes as InformationModesContentType)
                                        .items.length !== 0 &&
                                    saleMethod.active === true
                                );
                            }
                        )[0].name
                    );
                }

                setGeneralTransition(GENERAL_TRANSITION_INITIAL_STATE);
                setGeneralChosenTransition(NONE);
                setIsSpecificTransitionChosen(false);
                if (template.pages.ways.printerOptions !== undefined) {
                    template.pages.ways.printerOptions = {
                        ...template.pages.ways.printerOptions,
                        actionsButtons: {
                            ...(template.pages.ways
                                .printerOptions as PrinterOptionsPageType)
                                .actionsButtons,
                            items: (template.pages.ways
                                .printerOptions as PrinterOptionsPageType).actionsButtons.items.map(
                                (item) => ({ ...item, active: true })
                            ),
                        },
                    };
                } else {
                    template.pages.elements.printerOptions = {
                        ...template.pages.elements.printerOptions,
                        actionsButtons: {
                            ...(template.pages.elements
                                .printerOptions as PrinterOptionsPageType)
                                .actionsButtons,
                            items: (template.pages.elements
                                .printerOptions as PrinterOptionsPageType).actionsButtons.items.map(
                                (item) => ({ ...item, active: true })
                            ),
                        },
                    };
                }

                if (template.pages.subSteps.customerBenefit === undefined) {
                    template = {
                        ...template,
                        pages: {
                            ...template.pages,
                            subSteps: {
                                ...template.pages.subSteps,
                                customerBenefit: CUSTOMER_BENEFIT_INITIAL_STATE as any,
                            },
                        },
                    };
                }
                if (Object.keys(template.pages.ways).length === 0) {
                    const localWays: (
                        | PageType
                        | ConsommationPageType
                        | StandbyScreenPageType
                        | PaymentPageType
                        | OrderTakingPageType
                        | PrinterOptionsPageType
                    )[] = Object.values(template.pages.elements)
                        .filter((itemElement: any) =>
                            VALID_WAYS_PAGES.includes(itemElement.name)
                        )
                        .map(
                            (
                                item:
                                    | PageType
                                    | ConsommationPageType
                                    | StandbyScreenPageType
                                    | PaymentPageType
                                    | OrderTakingPageType
                                    | PrinterOptionsPageType
                            ) => ({
                                ...item,
                                prefix: WAYS_PAGES,
                                animation: NONE,
                            })
                        );

                    const localElements: (
                        | PageType
                        | ConsommationPageType
                        | StandbyScreenPageType
                        | PaymentPageType
                        | OrderTakingPageType
                        | PrinterOptionsPageType
                    )[] = Object.values(template.pages.elements)
                        .filter((itemElement) =>
                            DEFAULT_ELEMENTS_PAGES.includes(itemElement.name)
                        )
                        .map(
                            (
                                item:
                                    | PageType
                                    | ConsommationPageType
                                    | StandbyScreenPageType
                                    | PaymentPageType
                                    | OrderTakingPageType
                                    | PrinterOptionsPageType
                            ) => ({
                                ...item,
                                prefix: ELEMENTS_PAGES,
                            })
                        );
                    const newLocalProjectWays: ItemsType = {};

                    localWays.forEach((item, index) => {
                        newLocalProjectWays[item.name] = {
                            ...item,
                            index: index,
                        };
                    });

                    const newLocalProjectElements: ItemsType = {};

                    localElements.forEach((item, index) => {
                        newLocalProjectElements[item.name] = {
                            ...item,
                            index: index,
                        };
                    });
                    setItems({
                        ...items,
                        ways: newLocalProjectWays,
                        elements: newLocalProjectElements,
                        subSteps: template.pages.subSteps,
                    });
                    setDefaultWays({
                        ...items,
                        ways: newLocalProjectWays,
                        elements: newLocalProjectElements,
                        subSteps: template.pages.subSteps,
                    });
                } else {
                    setItems(template.pages);
                    setDefaultWays(template.pages);
                }

                setIsConfigured(true);
                let paymentInformationItem = (template.content
                    .payment as PaymentContentItemType).items.filter(
                    (regulationMode) => {
                        return (
                            regulationMode.shortName === CREDIT_CARD_SHORT_NAME
                        );
                    }
                )[0];
                for (let language of Object.keys(
                    (paymentInformationItem as PaymentMainContentItemType)
                        .languages
                )) {
                    setInformationMessageItem({
                        ...paymentInformationItem.languages[language].amount
                            .informationMessage,
                        name: paymentInformationItem.languages[
                            language
                        ].amount.informationMessage.name.replace("£", " €"),
                        defaultName: paymentInformationItem.languages[
                            language
                        ].amount.informationMessage.defaultName.replace(
                            "£",
                            " €"
                        ),
                    });
                }
            }
        } catch (error) {
            console.error("Error:", error);
            swal({
                icon: "error",
                content: <p>{t("Failed to configure template")}</p>,
                buttons: false,
                timer: 2000,
            });
        }
    }

    function handleResetTemplateOnclickEvent() {
        handleChosenTemplateOnclickEvent();
        setImageKey(uuid());
        setIsModalOpened(false);
    }

    const closeModal = () => {
        setModalVisibility(false);
    };

    return (
        <React.Fragment>
            <React.Fragment>
                <div>
                    <FlexboxGrid className="flex-column">
                        <FlexboxGrid
                            alignItemsCentered={true}
                            styles={{ height: "40px", fontSize: "15px" }}
                        >
                            {template.name}
                        </FlexboxGrid>
                        <FlexboxGrid
                            styles={{
                                width: "270px",
                                height: "470px",
                                border: "1px solid #BCBCBC",
                                borderRadius: "1rem",
                                gap: "5px",
                                borderBottom:
                                    chosenTemplate.name === template.name
                                        ? "10px solid #34C38F"
                                        : "1px solid #BCBCBC",
                            }}
                            className="flex-column"
                        >
                            <img
                                src={`./images/${template.coverImage}`}
                                alt={template.name}
                                style={{
                                    height: "400px",
                                    width: "268px",
                                    objectFit: "fill",
                                    borderTopRightRadius: "1rem",
                                    borderTopLeftRadius: "1rem",
                                }}
                            />
                            <FlexboxGrid
                                gap="5px"
                                alignItemsCentered={true}
                                justifyContentCentered={true}
                            >
                                <StyledButton
                                    rounded={true}
                                    className="pme_btn_buttonSelectTemplate"
                                    variant={
                                        chosenTemplate.name === template.name
                                            ? "success"
                                            : mainColor
                                    }
                                    onClick={() => {
                                        if (isConfigured === false) {
                                            handleChosenTemplateOnclickEvent();
                                        }
                                    }}
                                >
                                    {chosenTemplate.name === template.name
                                        ? t("Selected")
                                        : t("Choose")}
                                </StyledButton>
                                {chosenTemplate.name === template.name ? (
                                    <StyledIconButton
                                        disabled={
                                            project.Status === "Publié"
                                                ? true
                                                : false
                                        }
                                        title={t("Reset")}
                                        outline={true}
                                        variant="danger"
                                        className="m-0 pme_btn_buttonReinitializeTemplate"
                                        onClick={() => {
                                            if (project.Status !== "Publié") {
                                                setIsModalOpened(
                                                    !isModalOpened
                                                );
                                            }
                                        }}
                                    >
                                        <RefreshIcon height={20} width={20} />
                                    </StyledIconButton>
                                ) : null}
                                <StyledIconButton
                                    rounded={true}
                                    className="m-0 bg-dark pme_btn_buttonVisualizeTemplate"
                                    onClick={() => {
                                        setModalVisibility(true);
                                        setVisualizedTemplate(template);
                                    }}
                                >
                                    <EyeIcon
                                        height={20}
                                        width={20}
                                        fill="white"
                                    />
                                </StyledIconButton>
                            </FlexboxGrid>
                        </FlexboxGrid>
                    </FlexboxGrid>
                    <Modal
                        isOpen={isModalOpened}
                        toggle={toggle}
                        backdrop={false}
                        fade={false}
                        centered
                    >
                        <ModalHeader toggle={toggle}>{t("Alert")}</ModalHeader>
                        <ModalBody>
                            <div>
                                {t(
                                    "Resetting the template will cause you to lose all changes made"
                                )}
                            </div>
                            <FlexboxGrid
                                justifyContentCentered={true}
                                className="pt-4"
                                gap="10px"
                            >
                                <CustomMainColorButton
                                    rounded={true}
                                    variant="primary"
                                    outline
                                    onClick={() => setIsModalOpened(false)}
                                >
                                    {t("Cancel")}
                                </CustomMainColorButton>
                                <StyledButton
                                    rounded={true}
                                    variant="danger"
                                    onClick={handleResetTemplateOnclickEvent}
                                >
                                    {t("Reset")}
                                </StyledButton>
                            </FlexboxGrid>
                        </ModalBody>
                    </Modal>
                </div>
                <Modal
                    isOpen={isAlertModalOpened}
                    toggle={alertToggle}
                    backdrop={false}
                    fade={false}
                    centered
                >
                    <ModalHeader toggle={alertToggle}>
                        <span
                            style={{
                                textAlign: "left",
                                font: " normal normal 600 25px Segoe UI",
                                color: "#000000",
                            }}
                        >
                            {" "}
                            {t("Alert")}
                        </span>
                    </ModalHeader>
                    <ModalBody
                        className="d-flex flex-column"
                        style={{ gap: "10px" }}
                    >
                        <div
                            style={{
                                textAlign: "center",
                                font: " normal normal 600 16px/10px Segoe UI",
                                color: "#2B2828",
                            }}
                        >
                            {t("Warning! Missing setting.")}
                        </div>
                        <div
                            style={{
                                font: " normal normal 600 16px/20px Segoe UI",
                                textAlign: "left",
                                color: "#2B2828",
                                padding: "14px",
                            }}
                        >
                            {t(
                                "To configure your KIOSK, it is essential to configure the elements in the general configuration section."
                            )}
                        </div>
                        <FlexboxGrid
                            className="pt-4 justify-content-end"
                            gap="10px"
                        >
                            <CustomMainColorButton
                                rounded
                                variant="primary"
                                onClick={() => setIsAlertModalOpened(false)}
                            >
                                {t("Close")}
                            </CustomMainColorButton>
                        </FlexboxGrid>
                    </ModalBody>
                </Modal>
            </React.Fragment>
            {isModalShown && (
                <Modala close={closeModal}>
                    <div
                        style={{
                            height: "940px",
                            display: "grid",
                            gridTemplateRows: "0.001fr 0.999fr",
                            marginTop: "-185px",
                            marginLeft: "-280px",
                            borderRadius: "9px",
                            background:
                                "linear-gradient(227.9deg, #1F1726 0%, #120E16 100%)",
                            boxShadow:
                                "-5px -5px 11px #221A2B, 5px 5px 11px #16101C",
                            position: "fixed",
                            zIndex: 500000,
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "start",
                                justifyContent: "end",
                                padding: "10px",
                            }}
                        >
                            <CrossIcon
                                fill="white"
                                height={10}
                                width={10}
                                onClick={() => {
                                    closeModal();
                                }}
                            />
                        </div>

                        <SwiperWrapper />
                    </div>
                </Modala>
            )}
        </React.Fragment>
    );
}
